import React, { useEffect, useMemo, useState } from 'react';
import { currencyFormatter } from '../utils/reportingUtils';

const axios = require('axios').default;
const qs = require('qs');

const ACTIVITY_COLUMNS = [
  { id: 'price', label: 'Price' },
  { id: 'fees', label: 'Fees' },
  { id: 'gross', label: 'Gross' },
  { id: 'tax', label: 'Tax' },
  { id: 'net', label: 'Net' },
  { id: 'card', label: 'Card' },
  { id: 'exchanged', label: 'Exchanged' },
];

const cardStyle = {
  background: 'var(--sl-bg-primary, #fff)',
  border: '1px solid var(--sl-border-secondary, #e4e7ec)',
  borderRadius: 8,
  padding: 16,
  marginBottom: 16,
};
const headingStyle = { fontSize: 14, fontWeight: 600, color: 'var(--sl-text-primary, #101828)', marginBottom: 8 };
const mutedStyle = { fontSize: 13, color: 'var(--sl-text-tertiary, #667085)' };
const money = (amount) => currencyFormatter.format(amount || 0);

const summaryParams = (dateRange, activeFilters, filterIds) => {
  const params = {};
  filterIds.forEach((id) => {
    if (activeFilters?.[id]?.length) params[id] = activeFilters[id];
  });
  if (dateRange?.start) params.start_date = dateRange.start;
  if (dateRange?.end) params.end_date = dateRange.end;
  return params;
};

function TotalsTable({ title, rows, total, totalLabel = 'Total:', columns = [{ id: 'total', label: 'Total' }] }) {
  return (
    <div style={cardStyle}>
      <div style={headingStyle}>{title}</div>
      {rows.length === 0 ? <div style={mutedStyle}>None</div> : (
        <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
          <thead>
            <tr>
              <th />
              {columns.map((col) => <th key={col.id} className="text-right">{col.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <td>{row.label}</td>
                {columns.map((col) => <td key={col.id} className="text-right">{money(row[col.id])}</td>)}
              </tr>
            ))}
            <tr style={{ fontWeight: 600 }}>
              <td>{totalLabel}</td>
              {columns.map((col, index) => (
                <td key={col.id} className="text-right">{index === columns.length - 1 ? money(total) : ''}</td>
              ))}
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

function ActivitySection({ section }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
        <thead>
          <tr>
            <th>{section.label}</th>
            <th>Account Code</th>
            {ACTIVITY_COLUMNS.map((col) => <th key={col.id} className="text-right">{col.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {section.rows.length === 0 ? (
            <tr><td colSpan={ACTIVITY_COLUMNS.length + 2} style={mutedStyle}>{`No ${section.label}`}</td></tr>
          ) : section.rows.map((row) => (
            <tr key={row.id}>
              <td>{row.name || '—'}</td>
              <td>{row.account_code}</td>
              {ACTIVITY_COLUMNS.map((col) => <td key={col.id} className="text-right">{money(row[col.id])}</td>)}
            </tr>
          ))}
          {section.rows.length > 0 && (
            <tr style={{ fontWeight: 600 }}>
              <td colSpan={2}>{`${section.label} Total:`}</td>
              {ACTIVITY_COLUMNS.map((col) => <td key={col.id} className="text-right">{money(section.totals[col.id])}</td>)}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function PaymentsActivitiesSummary({ team, csrfToken, dateRange, activeFilters, filterIds }) {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const params = useMemo(
    () => summaryParams(dateRange, activeFilters, filterIds),
    [dateRange, activeFilters, filterIds],
  );
  const query = qs.stringify(params, { arrayFormat: 'brackets' });

  useEffect(() => {
    if (!team?.id) return undefined;
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    let ignore = false;
    setError(null);
    axios.get(`/teams/${team.id}/reporting/finances/payments/summary.json?${query}`)
      .then((res) => { if (!ignore) setSummary(res.data); })
      .catch(() => { if (!ignore) setError('Could not load the payments & activities summary.'); });
    return () => { ignore = true; };
  }, [team?.id, csrfToken, query]);

  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!summary) return <div style={{ ...cardStyle, ...mutedStyle }}>Loading summary…</div>;

  const { payments_by_type: byType, card_summary: cards, merchant_summary: merchants, activities, reconciliation } = summary;

  return (
    <div data-testid="payments-activities-summary" style={{ marginBottom: 24 }}>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div style={{ fontSize: 16, fontWeight: 600 }}>Daily Breakdown Summary</div>
        <a className="btn btn-sm btn-outline-secondary" href={`/teams/${team.id}/reporting/finances/payments/summary/export.csv?${query}`}>
          Export Summary CSV
        </a>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
        <TotalsTable
          title="Payments by Type"
          rows={byType.rows}
          total={byType.total}
          totalLabel="Payments Total Received:"
          columns={[{ id: 'payments', label: 'Received' }, { id: 'refunds', label: 'Refunded' }, { id: 'total', label: 'Total' }]}
        />
        <TotalsTable title="Card Summary" rows={cards.rows} total={cards.total} />
        <TotalsTable title="Merchant Summary" rows={merchants.rows} total={merchants.total} />
      </div>

      <div style={cardStyle}>
        <div style={headingStyle}>Activities Breakdown</div>
        <div style={{ ...mutedStyle, marginBottom: 12 }}>
          From the General Ledger&apos;s postings. Account Code is the event&apos;s GL code. Price and fees exclude tax;
          gross includes it. Card is the part of each activity its orders paid by card.
        </div>
        {activities.sections.map((section) => <ActivitySection key={section.key} section={section} />)}
        <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
          <thead>
            <tr>
              <th>Activities Total</th>
              {ACTIVITY_COLUMNS.map((col) => <th key={col.id} className="text-right">{col.label}</th>)}
            </tr>
          </thead>
          <tbody>
            <tr style={{ fontWeight: 600 }}>
              <td />
              {ACTIVITY_COLUMNS.map((col) => <td key={col.id} className="text-right">{money(activities.totals[col.id])}</td>)}
            </tr>
          </tbody>
        </table>
      </div>

      <div
        data-testid="payments-reconciliation"
        className={`alert ${reconciliation.reconciled ? 'alert-success' : 'alert-warning'}`}
        style={{ fontSize: 13 }}
      >
        {`Payments Total Received ${money(reconciliation.payments_total_received)} · `}
        {`Activities Total (gross) ${money(reconciliation.activities_total_gross)} · `}
        {`General Ledger ${money(reconciliation.general_ledger_total)}`}
        {reconciliation.reconciled
          ? ' — reconciled.'
          : ` — ${money(reconciliation.difference)} of payments isn't matched by General Ledger postings in this range (fees or tax not on the ledger, or a payment taken on a different day than its sale).`}
      </div>
    </div>
  );
}

export default PaymentsActivitiesSummary;
